import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';

/**
 * AddRoomModal
 *
 * Props:
 *  - isOpen               : boolean
 *  - onClose              : () => void
 *  - onSaveRoom           : (roomPayload) => void — payload includes floorId
 *  - floors               : Array<{ id, floorName }> — full floor objects from backend
 *  - roomToEdit           : object | null — normalised room from InpatientManage
 *  - configuredRoomTypes  : Array<any> — optional room types passed from parent
 *  - configuredBedTypes   : Array<any> — optional bed types passed from parent
 */
const AddRoomModal = ({
  isOpen,
  onClose,
  onSaveRoom,
  floors,
  roomToEdit,
  configuredRoomTypes,
  configuredBedTypes
}) => {
  const [floorId, setFloorId] = useState('');
  const [roomType, setRoomType] = useState('');
  const [bedType, setBedType] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [totalBeds, setTotalBeds] = useState(1);
  const [status, setStatus] = useState('Available');

  const [backendRoomTypes, setBackendRoomTypes] = useState([]);
  const [backendBedTypes, setBackendBedTypes] = useState([]);

  // Fetch configured room & bed types directly from backend when modal opens
  useEffect(() => {
    if (isOpen) {
      Promise.all([
        axios.get(ENDPOINTS.GET_ALL_ROOM_TYPES).catch(() => ({ data: [] })),
        axios.get(ENDPOINTS.GET_ALL_BED_TYPES).catch(() => ({ data: [] }))
      ]).then(([roomTypesRes, bedTypesRes]) => {
        if (Array.isArray(roomTypesRes.data)) {
          setBackendRoomTypes(roomTypesRes.data);
        }
        if (Array.isArray(bedTypesRes.data)) {
          setBackendBedTypes(bedTypesRes.data);
        }
      });
    }
  }, [isOpen]);

  // Build Room Type items with active/inactive status
  const roomTypeItems = [];
  const addedRoomTypeNames = new Set();

  (backendRoomTypes || []).forEach((rt) => {
    if (rt.name && !addedRoomTypeNames.has(rt.name.toLowerCase())) {
      addedRoomTypeNames.add(rt.name.toLowerCase());
      roomTypeItems.push({
        name: rt.name,
        status: rt.status || 'Active',
        isInactive: rt.status === 'Inactive'
      });
    }
  });

  (configuredRoomTypes || []).forEach((t) => {
    const name = typeof t === 'string' ? t : t.name || t.roomType;
    if (name && !addedRoomTypeNames.has(name.toLowerCase())) {
      addedRoomTypeNames.add(name.toLowerCase());
      const st = typeof t === 'object' && t.status ? t.status : 'Active';
      roomTypeItems.push({
        name: name,
        status: st,
        isInactive: st === 'Inactive'
      });
    }
  });

  if (roomToEdit?.roomType && !addedRoomTypeNames.has(roomToEdit.roomType.toLowerCase())) {
    roomTypeItems.push({
      name: roomToEdit.roomType,
      status: 'Active',
      isInactive: false
    });
  }

  // Build Bed Type items with active/inactive status
  const bedTypeItems = [];
  const addedBedTypeNames = new Set();

  (backendBedTypes || []).forEach((bt) => {
    if (bt.name && !addedBedTypeNames.has(bt.name.toLowerCase())) {
      addedBedTypeNames.add(bt.name.toLowerCase());
      bedTypeItems.push({
        name: bt.name,
        status: bt.status || 'Active',
        isInactive: bt.status === 'Inactive'
      });
    }
  });

  (configuredBedTypes || []).forEach((t) => {
    const name = typeof t === 'string' ? t : t.name || t.bedType;
    if (name && !addedBedTypeNames.has(name.toLowerCase())) {
      addedBedTypeNames.add(name.toLowerCase());
      const st = typeof t === 'object' && t.status ? t.status : 'Active';
      bedTypeItems.push({
        name: name,
        status: st,
        isInactive: st === 'Inactive'
      });
    }
  });

  if (roomToEdit?.bedType && !addedBedTypeNames.has(roomToEdit.bedType.toLowerCase())) {
    bedTypeItems.push({
      name: roomToEdit.bedType,
      status: 'Active',
      isInactive: false
    });
  }

  // Calculate actual occupied beds for roomToEdit
  const currentOccupied = roomToEdit
    ? Number(
        roomToEdit.occupiedBeds !== undefined
          ? roomToEdit.occupiedBeds
          : roomToEdit.status === 'Occupied'
          ? roomToEdit.totalBeds || 1
          : 0
      )
    : 0;

  useEffect(() => {
    if (roomToEdit) {
      const matchedFloor = floors?.find(
        (f) => f.id === roomToEdit.floorId || f.floorName === roomToEdit.floor
      );
      setFloorId(matchedFloor ? String(matchedFloor.id) : '');
      setRoomType(roomToEdit.roomType || roomTypeItems.find((r) => !r.isInactive)?.name || '');
      setBedType(roomToEdit.bedType || bedTypeItems.find((b) => !b.isInactive)?.name || '');
      setRoomNumber(roomToEdit.room || '');
      setTotalBeds(roomToEdit.totalBeds || 1);
      setStatus(roomToEdit.status === 'Under Maintenance' ? 'Under Maintenance' : 'Available');
    } else {
      setFloorId(floors && floors.length > 0 ? String(floors[0].id) : '');
      const firstActiveRoomType = roomTypeItems.find((r) => !r.isInactive)?.name || '';
      const firstActiveBedType = bedTypeItems.find((b) => !b.isInactive)?.name || '';
      setRoomType(firstActiveRoomType);
      setBedType(firstActiveBedType);
      setRoomNumber('');
      setTotalBeds(1);
      setStatus('Available');
    }
  }, [roomToEdit, floors, isOpen, backendRoomTypes.length, backendBedTypes.length]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!floorId) {
      toast.error('Please select a floor');
      return;
    }
    const finalRoomType = roomType.trim();
    if (!finalRoomType) {
      toast.error('Please select a room type');
      return;
    }

    // Protect against adding inactive room type
    const matchedRt = roomTypeItems.find((r) => r.name.toLowerCase() === finalRoomType.toLowerCase());
    if (matchedRt && matchedRt.isInactive) {
      toast.error(`Cannot add or assign "${matchedRt.name}" because this Room Type is Inactive!`, {
        autoClose: 5000
      });
      return;
    }

    const finalBedType = bedType.trim();
    if (!finalBedType) {
      toast.error('Please select a bed type');
      return;
    }

    // Protect against adding inactive bed type
    const matchedBt = bedTypeItems.find((b) => b.name.toLowerCase() === finalBedType.toLowerCase());
    if (matchedBt && matchedBt.isInactive) {
      toast.error(`Cannot add or assign "${matchedBt.name}" because this Bed Type is Inactive!`, {
        autoClose: 5000
      });
      return;
    }

    if (!roomNumber.trim()) {
      toast.error('Please enter a room number/name');
      return;
    }
    if (!totalBeds || isNaN(totalBeds) || Number(totalBeds) < 1) {
      toast.error('Please enter valid total beds for this room');
      return;
    }

    const requestedTotalBeds = Number(totalBeds);

    // Block reducing total beds below currently occupied patient beds
    if (requestedTotalBeds < currentOccupied) {
      toast.error(
        `Cannot reduce total beds to ${requestedTotalBeds} because ${currentOccupied} bed(s) in this room are currently occupied by admitted patients!`,
        { autoClose: 5000 }
      );
      return;
    }

    // Block setting room to Under Maintenance if patient beds are currently occupied
    if (status === 'Under Maintenance' && currentOccupied > 0) {
      toast.error(
        `Cannot set room status to "Under Maintenance" because ${currentOccupied} bed(s) are currently occupied by admitted patients!`,
        { autoClose: 5000 }
      );
      return;
    }

    const selectedFloor = floors?.find((f) => String(f.id) === String(floorId));

    const payload = {
      id: roomToEdit ? roomToEdit.id : null, // null = new room → POST
      floorId: Number(floorId),
      floor: selectedFloor?.floorName ?? '', // for display in UI without refetch
      roomType: finalRoomType,
      room: roomNumber.trim(),
      roomName: roomNumber.trim(),
      bedType: finalBedType,
      totalBeds: requestedTotalBeds,
      occupiedBeds: currentOccupied,
      status: currentOccupied === requestedTotalBeds && requestedTotalBeds > 0 ? 'Occupied' : status,
    };

    onSaveRoom(payload);
    onClose();
  };

  return (
    <div className="manage-modal-backdrop" onClick={onClose}>
      <div className="manage-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="manage-modal-header">
          <h3>🛏️ {roomToEdit ? 'Edit Hospital Room & Bed' : 'Add New Hospital Room & Bed'}</h3>
          <button className="manage-modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="manage-modal-body">
            {/* Floor Select */}
            <div className="modal-form-group">
              <label>* Floor</label>
              <select value={floorId} onChange={(e) => setFloorId(e.target.value)}>
                <option value="">-- Select Floor --</option>
                {floors && floors.map((fl) => (
                  <option key={fl.id} value={String(fl.id)}>{fl.floorName}</option>
                ))}
              </select>
            </div>

            {/* Room Type Select (Dynamic with Inactive in Red & Disabled) */}
            <div className="modal-form-group">
              <label>* Room Type</label>
              <select
                value={roomType}
                onChange={(e) => setRoomType(e.target.value)}
              >
                {roomTypeItems.length === 0 ? (
                  <option value="" disabled>-- No Room Types Configured (Add via Room Types) --</option>
                ) : (
                  <>
                    <option value="">-- Select Room Type --</option>
                    {roomTypeItems.map((item) => (
                      <option
                        key={item.name}
                        value={item.name}
                        disabled={item.isInactive}
                        style={{
                          color: item.isInactive ? '#dc2626' : '#0f172a',
                          fontWeight: item.isInactive ? '600' : 'normal',
                          backgroundColor: item.isInactive ? '#fef2f2' : '#ffffff'
                        }}
                      >
                        {item.name} {item.isInactive ? '(Inactive)' : ''}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            {/* Room Identifier / Name */}
            <div className="modal-form-group">
              <label>* Room Identifier / Name</label>
              <input
                type="text"
                placeholder="e.g. Room 101, Ward Bed A-4, ICU-02"
                value={roomNumber}
                onChange={(e) => setRoomNumber(e.target.value)}
                required
              />
            </div>

            {/* Total Beds Capacity */}
            <div className="modal-form-group">
              <label>* Total Beds Capacity for this Room</label>
              <input
                type="number"
                min="1"
                max="100"
                value={totalBeds}
                onChange={(e) => setTotalBeds(e.target.value)}
                required
              />
              {currentOccupied > 0 && (
                <span style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px', display: 'block' }}>
                  ⚠️ Currently occupied by {currentOccupied} patient(s). Total beds cannot be set below {currentOccupied}.
                </span>
              )}
            </div>

            {/* Bed Type Select (Dynamic with Inactive in Red & Disabled) */}
            <div className="modal-form-group">
              <label>* Bed Type</label>
              <select
                value={bedType}
                onChange={(e) => setBedType(e.target.value)}
              >
                {bedTypeItems.length === 0 ? (
                  <option value="" disabled>-- No Bed Types Configured (Add via Bed Types) --</option>
                ) : (
                  <>
                    <option value="">-- Select Bed Type --</option>
                    {bedTypeItems.map((item) => (
                      <option
                        key={item.name}
                        value={item.name}
                        disabled={item.isInactive}
                        style={{
                          color: item.isInactive ? '#dc2626' : '#0f172a',
                          fontWeight: item.isInactive ? '600' : 'normal',
                          backgroundColor: item.isInactive ? '#fef2f2' : '#ffffff'
                        }}
                      >
                        {item.name} {item.isInactive ? '(Inactive)' : ''}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            {/* Operational Status */}
            <div className="modal-form-group">
              <label>* Operational Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={currentOccupied > 0}
              >
                <option value="Available">Available</option>
                <option value="Under Maintenance">Under Maintenance</option>
              </select>
              {currentOccupied > 0 && (
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                  Status locked while occupied by admitted patients.
                </span>
              )}
            </div>
          </div>

          <div className="manage-modal-footer">
            <button type="button" className="modal-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-btn-save">
              {roomToEdit ? 'Save Changes' : 'Save Room'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRoomModal;
